import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { FirebaseError } from 'firebase/app';
import { firebaseConfig } from '@/config/firebase-config';

// Firebase Config Debug

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Only initialize analytics on the client side with dynamic import
let analytics: any = null;
let analyticsPromise: Promise<any> | null = null;

export const getFirebaseAnalytics = async () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  if (analytics) {
    return analytics;
  }
  
  if (!analyticsPromise) {
    analyticsPromise = import('firebase/analytics').then((module) => {
      analytics = module.getAnalytics(app);
      return analytics;
    });
  }
  
  return analyticsPromise;
};

export { analytics };

/**
 * Get a user-friendly error message from a Firebase Auth error
 * @param error The error thrown by Firebase Auth
 * @returns A user-friendly error message
 */
export function getAuthErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    // Handle specific Firebase Auth error codes with user-friendly messages
    switch (error.code) {
      case 'auth/wrong-password':
        return 'Invalid password. Please check your password and try again.';
      case 'auth/user-not-found':
        return 'User not found. Please check your email and try again.';
      case 'auth/invalid-credential':
        return 'Invalid credentials. Please check your email and password.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later.';
      case 'auth/requires-recent-login':
        return 'This operation requires a more recent login. Please log out and log back in.';
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please use a different email or try logging in.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use a stronger password.';
      case 'auth/invalid-email':
        return 'Invalid email format. Please enter a valid email address.';
      default:
        return error.message || 'An authentication error occurred.';
    }
  }
  
  // For non-Firebase errors or unknown errors
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unknown error occurred. Please try again.';
}